import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { CreateStockTransactionDto, ProductBatchDto, StockReason, StorageLocationDto } from '@services/dtos'
import { stockTransactionSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Alert, Autocomplete, Grid, TextField, Tooltip } from '@mui/material'
import { createStockTransaction } from '@state/reducers/stockTransactionReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { productBatchService } from '@services/ProductBatchService'
import { storageLocationService } from '@services/storageLocationService'
import { StockReasonLabels, StockReasonOperators } from '@utils/constants'
import { FormHeader } from './FormHeader'

const REASON_OPTIONS = (Object.entries(StockReasonLabels) as Array<[StockReason, string]>)
  .filter(([reason]) => reason !== StockReason.RECEIPT && reason !== StockReason.TRANSFER_IN)
  .concat([[StockReason.TRANSFER_OUT, 'Transfert']])
  .map(([value, label]) => ({ value, label }))

type Props = {
  onClose: () => void
}

const StockTransactionForm: React.FC<Props> = ({
  onClose
}) => {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedProductBatch, setSelectedProductBatch] = useState<ProductBatchDto | null>(null)

  const {
    handleSubmit,
    reset,
    control,
    watch,
    setValue, 
    formState: { errors },
  } = useForm<CreateStockTransactionDto>({
    resolver: yupResolver(stockTransactionSchema),
    context: { selectedProductBatch },
  })

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateStockTransactionDto) => {
    setIsSubmitting(true)

    dispatch(createStockTransaction(data))
      .unwrap()
      .then(() => {
        cb('La transaction a été créée avec succès')
        onClose()
      })
      .catch(() => cb('Une erreur est survenue'))
      .finally(() => setIsSubmitting(false))
  }

  const fields =  [
    { name: 'unitsDelta', label: 'Quantité', sm: 6, type: 'number' },
    { name: 'reference', label: 'Référence', sm: 12 },
    { name: 'notes', label: 'Notes', type: 'textarea', sm: 12 },
  ]

  const fetchProductBatches = async (search: string) => {
    const page = await productBatchService.getAll({ search })
    return page?.datas ?? []
  }
  const fetchStorageLocations = async (search: string): Promise<StorageLocationDto[]> => {
    const response = await storageLocationService.getAll({ search })
    return response?.datas.filter(({ id }) => id !== storageLocationId) ?? []
  }

  const storageLocationId = watch('storageLocationId')
  const reason = watch('reason')

  useEffect(() => {
    if (reason === StockReason.EXPIRATION && selectedProductBatch) {
      setValue('unitsDelta', selectedProductBatch.totalUnits)
    }
  }, [reason, selectedProductBatch, setValue])

  const renderFields = (fields: { name: string; label: string; type?: string; sm?: number }[]) => {
    return fields.map((field) => {
      if (field.name !== 'unitsDelta') {
        return <Grid item xs={12} sm={field.sm ? field.sm : 6} key={field.name}>
          <InputField
            control={control}
            name={field.name as keyof CreateStockTransactionDto}
            label={field.label}
            type={field.type ?? 'text'}
            multiline={field.type === 'textarea'}
            rows={field.type === 'textarea' ? 2 : undefined}
          />
        </Grid>
      }

      const Input = <InputField
        control={control}
        name={field.name as keyof CreateStockTransactionDto}
        label={field.label}
        preventNegative
        type={field.type}
        disabled={reason === StockReason.EXPIRATION}
      />
      
      return <Grid item xs={12} sm={field.sm ? field.sm : 6} key={field.name}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {reason && (
            <span className="font-bold">
              ({StockReasonOperators[reason]})
            </span>
          )}
          {reason === StockReason.EXPIRATION 
            ? <Tooltip title="L'ensemble du lot restant sera expiré" arrow>
              <span style={{ display: 'inline-flex' }}>{Input}</span>
            </Tooltip> 
            : Input}
        </div>
      </Grid>
    })
  }

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title={'Ajout'}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Transaction de stock
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12} sm={6}>
                <SelectField
                  control={control}
                  name="reason"
                  options={REASON_OPTIONS}
                  value={watch('reason')}
                  label="Type de mouvement"
                />
              </Grid>
              {renderFields([fields[0]])}
              <Grid item xs={12}>
                <ServerAutocomplete<ProductBatchDto>
                  fetchService={fetchProductBatches}
                  onChange={(value: ProductBatchDto | null) => {
                    setValue('productBatchId', value?.id ?? '')
                    setSelectedProductBatch(value)
                    if (value && value.storageLocations.length === 1) {
                      setValue('storageLocationId', value.storageLocations[0].storageLocation.id)
                    }
                  }}
                  getValue={(value) => value?.id?.toString() || null}
                  getOptionLabel={(value) => {
                    const productBatch = value as ProductBatchDto
                    return productBatch ? `${productBatch.lotNumber} (${productBatch.productCatalog.name})` : ''
                  }}
                  debounceMs={300}
                  placeholder="Rechercher un lot de produits (n° de lot ou nom du produit)"
                  label="Lot de produits"
                  helperText={errors.productBatchId?.message}
                  error={!!errors.productBatchId}
                  minSearchLength={0}
                  initialOptions={
                    selectedProductBatch
                      ? [selectedProductBatch]
                      : []
                  }
                  value={selectedProductBatch}
                />
                {selectedProductBatch && reason !== StockReason.EXPIRATION && storageLocationId && (
                  <Alert severity="info" className="mt-2">
                    Quantité du lot dans ce stockage : <span className="font-bold">
                      {selectedProductBatch.storageLocations.find(el => el.storageLocation.id === storageLocationId)?.units ?? 0}
                    </span>
                  </Alert>
                )}
              </Grid>
              {selectedProductBatch && reason !== StockReason.EXPIRATION && <Grid item xs={12}>
                <Autocomplete
                  options={selectedProductBatch?.storageLocations ?? []}
                  getOptionLabel={(option) => option.storageLocation.name}
                  value={
                    selectedProductBatch?.storageLocations.find(
                      sl => sl.storageLocation.id === storageLocationId
                    ) ?? null
                  }
                  onChange={(_event, value) => { setValue('storageLocationId', value?.storageLocation.id ?? null)}}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Stockage${reason === StockReason.TRANSFER_OUT ? ' source' : ''}`}
                      placeholder="Sélectionner un stockage"
                      disabled={!selectedProductBatch}
                      error={!!errors.storageLocationId}
                      helperText={errors.storageLocationId?.message}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.storageLocation.id === value.storageLocation.id}
                />
              </Grid>}
              {reason === StockReason.TRANSFER_OUT && storageLocationId && (<Grid item xs={12}>
                <ServerAutocomplete<StorageLocationDto>
                  autoFetchOnMount
                  fetchService={fetchStorageLocations}
                  onChange={(value: StorageLocationDto | null) => {
                    setValue('targetStorageLocationId', value?.id ?? '')
                  }}
                  getValue={(value) => value?.id?.toString() || null}
                  getOptionLabel={(value) => {
                    const storageLocation = value as StorageLocationDto
                    return storageLocation ? storageLocation.name : ''
                  }}
                  debounceMs={300}
                  placeholder="Rechercher un stockage"
                  label="Stockage cible"
                  helperText={errors.targetStorageLocationId?.message}
                  error={!!errors.targetStorageLocationId}
                  minSearchLength={0}
                />
              </Grid>)}
              {renderFields([fields[1], fields[2]])}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default StockTransactionForm
